import { useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import type { Post } from '../schema-copy/feed-schema';
import { createPostElement, type PostEntry } from './post-dom-factory';
import { loadExternalScripts, ensureSiteCss } from 't-lib-postviewer';
import { isEmbedScriptAllowed } from './embed-consent';
import { usePreferences } from '../connector/preferences';
import 't-lib-postviewer/css/postviewer.css';
import 't-lib-postviewer/css/post-tailwind.css';
import 't-lib-postviewer/css/post-tailwind-responsive.css';
import './PostView.css';
import './SinglePostView.css';

interface SinglePostViewProps {
  post: Post;
  feedId?: string;
  feedName?: string;
  siteUrl?: string;
  /** The feed's per-site stylesheet hash (RestFeedUpdate.siteCss), loaded when set. */
  siteCss?: string | null;
}

// Renders ONE full post using the exact DOM primitive the reader uses
// (createPostElement) so previews/share surfaces get the real HTML
// sanitization + embed (Instagram/TikTok/digest/image-only) hydration
// pipeline for free. The reader-only action buttons (mark read / star /
// read-later / share) are stripped — a preview keeps just the post and
// its "Original Post" link. The factory, PostRenderer and PostEntry are
// intentionally left untouched so the main reader cannot regress.
const SinglePostView: React.FC<SinglePostViewProps> = ({ post, feedId = '', feedName = '', siteUrl, siteCss }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [preferences] = usePreferences();

  const rehydrateEmbeds = useCallback(() => {
    if (containerRef.current) loadExternalScripts(post.externalScripts, containerRef.current, isEmbedScriptAllowed);
  }, [post.externalScripts]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const entry: PostEntry = {
      feedId,
      postIndex: post.number ?? 0,
      element: null,
      height: 0,
      loaded: true,
      data: post,
      isRead: false,
      initiallyRead: false,
      isStarred: false,
      isReadLater: false,
      manuallyUnread: false,
      keepUnread: false,
    };
    ensureSiteCss({ id: feedId, siteUrl, siteCss });
    const el = createPostElement(entry, feedName, siteUrl, 'full');
    // Drop the reader-only action UI; the "Original Post" link in
    // .cardActionsRow is a plain <a> (no data-action) and stays.
    el.querySelectorAll('.postActions, [data-action]').forEach(n => n.remove());
    container.replaceChildren(el);
    rehydrateEmbeds();
  }, [post, feedId, feedName, siteUrl, siteCss, rehydrateEmbeds]);

  // Twitter/X widgets need re-processing when the tab becomes visible again.
  useEffect(() => {
    if (!post.externalScripts?.includes('x-widget')) return;
    const onVisible = () => { if (!document.hidden) rehydrateEmbeds(); };
    window.addEventListener('pageshow', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('pageshow', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [post.externalScripts, rehydrateEmbeds]);

  return (
    <div
      className={`singlePostView${preferences.postFont == 'sans-serif' ? ' postFontSans' : ''}`}
      style={{ '--pv-font-size': `${preferences.postFontSize ?? 16}px` } as React.CSSProperties}
      ref={containerRef}
    />
  );
};

export default SinglePostView;
